import { Settings, X, Minimize2, Maximize2 } from 'lucide-react'
import { POOL_TOKENS, CISD_COLOR } from './index'

const MONTHS_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

function pad2(n) {
  return String(n).padStart(2, '0')
}

function formatTime(t, timeDisplay = 'local') {
  if (t == null) return '-'
  const seconds = t > 1e10 ? Math.floor(t / 1000) : t
  const date = new Date(seconds * 1000)
  if (Number.isNaN(date.getTime())) return '-'
  if (timeDisplay === 'utc') {
    return `${date.getUTCDate()} ${MONTHS_SHORT[date.getUTCMonth()]}, ${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}`
  }
  return date.toLocaleString('es-ES', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function LiquidityPoolsQuantumBadge({
  result,
  onOpenSettings,
  onClose,
  onMinimize,
  minimized = false,
  version = '2.0.0',
  onHeaderMouseDown,
  timeDisplay = 'local',
}) {
  const data = result || {}

  // Contadores 1:1 con el canvas: el render expone lo REALMENTE pintado en
  // _paintedCounts (pools activos / EQH / EQL / triángulos CISD). Los swings
  // son estado INTERNO del backend (canon §2): no se pintan ni se muestran.
  const painted = data._paintedCounts || null

  const rawPools = Array.isArray(data.pools) ? data.pools : []
  const rawCisd = Array.isArray(data.cisd_events) ? data.cisd_events : []

  const countPoolsActive = painted
    ? painted.poolsActivos
    : rawPools.filter((p) => String(p?.state || '').toLowerCase() !== 'barrido').length
  const countEqh = painted
    ? painted.eqh
    : rawPools.filter((p) => p?.kind === 'eqh' && String(p?.state || '').toLowerCase() !== 'barrido').length
  const countEql = painted
    ? painted.eql
    : rawPools.filter((p) => p?.kind === 'eql' && String(p?.state || '').toLowerCase() !== 'barrido').length

  const lastCisd = painted?.lastCisd ?? (() => {
    let last = null
    for (const ev of rawCisd) {
      const t = ev?.time ?? 0
      if (!last || t > (last.time ?? 0)) last = ev
    }
    return last
      ? { direction: last.direction, time: last.time, afterSweep: last.after_sweep === true }
      : null
  })()

  return (
    <div
      className="absolute top-2 left-2 z-10 flex flex-col gap-1 rounded-lg backdrop-blur-sm shadow-lg overflow-hidden min-w-[220px]"
      style={{ background: 'var(--paper, #FDFDFA)', border: '1px solid var(--hair, rgba(176,141,87,0.35))' }}
    >
      <div
        className="flex items-center gap-1.5 px-2.5 pt-2 pb-1 cursor-grab active:cursor-grabbing"
        onMouseDown={onHeaderMouseDown}
      >
        <span
          className="text-[11px] font-semibold tracking-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", color: 'var(--green-deep, #0B3D2E)' }}
        >
          Liquidity Pools Quantum <span className="opacity-60 font-normal">[{version}]</span>
          {minimized && (
            <span className="ml-1.5 text-[10px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>· {countPoolsActive} pools</span>
          )}
        </span>
        <button onClick={onOpenSettings} title="Configuración" className="ml-auto p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <Settings size={12} />
        </button>
        {minimized ? (
          <button onClick={() => onMinimize?.()} title="Expandir" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Maximize2 size={12} />
          </button>
        ) : (
          <button onClick={() => onMinimize?.()} title="Minimizar" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Minimize2 size={12} />
          </button>
        )}
        <button onClick={onClose} title="Ocultar dashboard" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <X size={12} />
        </button>
      </div>

      {!minimized && (
        <>
          <div className="flex flex-col gap-0.5 px-2.5 pb-1">
            <CountBar label="Pools activos" value={countPoolsActive} color={CISD_COLOR} />
            <CountBar label="EQH" value={countEqh} color={POOL_TOKENS.buy} />
            <CountBar label="EQL" value={countEql} color={POOL_TOKENS.sell} />
          </div>

          <div className="px-2.5 pb-1.5" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
            <div className="pt-1 flex items-center justify-between">
              <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>Último CISD</span>
              {lastCisd ? (
                <span className="text-[10px] font-medium flex items-center gap-1" style={{ color: CISD_COLOR }}>
                  <span>{lastCisd.direction === 'bull' ? '▲' : '▼'}</span>
                  <span>{formatTime(lastCisd.time, timeDisplay)}</span>
                  {lastCisd.afterSweep && (
                    <span
                      className="px-1 rounded text-[9px] font-semibold uppercase"
                      style={{ background: CISD_COLOR, color: '#15211B' }}
                    >
                      confluencia
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-[10px] font-medium" style={{ color: 'var(--ink-soft, #4A5A52)' }}>-</span>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function CountBar({ label, value, color }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>{label}</span>
      <span className="text-[10px] font-medium tabular-nums" style={{ color }}>{value}</span>
    </div>
  )
}
